/* fcp-warranty.js - keeps the free 2-year extended warranty in sync with the cart.
 *
 * Design notes (why it works this way):
 *  - The warranty variant is priced $0.00 with a compare-at of its real value. We do NOT
 *    use a Shopify discount: every active discount code on this store is combines_with
 *    false/false/false, so any automatic discount zeroing a priced warranty would be
 *    dropped the moment a shopper applies SAVE200 / FIRE100 / etc. and they would be
 *    charged for a "free" item. A $0 line cannot be un-zeroed by anything.
 *  - Third-party apps (SureBright, Seal, Kite, Profit AI) mutate the cart without
 *    publishing the theme's pubsub event, so we also observe fetch/XHR to /cart/*.
 *  - Every path funnels into one debounced, single-flight sync with a circuit breaker.
 */
(function () {
  'use strict';

  var CFG = window.FCP_WARRANTY_CONFIG || {};
  var PREVIEW_KEY = 'fcpWtyPreview';

  /* ---- activation ---- */
  try {
    var qs = new URLSearchParams(window.location.search);
    if (qs.get('fcpwty') === '1') sessionStorage.setItem(PREVIEW_KEY, '1');
    if (qs.get('fcpwty') === '0') sessionStorage.removeItem(PREVIEW_KEY);
  } catch (e) {}

  var preview = false;
  try { preview = sessionStorage.getItem(PREVIEW_KEY) === '1'; } catch (e) {}

  if (!CFG.warrantyVariantId) return;
  if (!CFG.enabled && !preview) return;
  if (preview) CFG.debug = true; /* internal preview sessions log their reasoning */

  var WTY = String(CFG.warrantyVariantId);
  var QUALIFY = (CFG.qualifyingProductIds || []).map(String);
  var R = CFG.routes || {};
  var CART = R.cart || '/cart';
  var PROPS = CFG.lineProperties || {};
  var MAX_QTY = CFG.maxQty || 10;

  var inFlight = null;
  var timer = null;
  var syncCount = 0;
  var MAX_SYNCS = 15;
  var depth = 0; /* > 0 while our own requests are in flight */

  function log() {
    if (!CFG.debug) return;
    try { console.log.apply(console, ['[fcp-warranty]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  function post(url, body) {
    depth++;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); }).then(
      function (v) { depth--; return v; },
      function (e) { depth--; throw e; }
    );
  }

  function getCart() {
    depth++;
    return fetch(CART + '.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.json(); }).then(
        function (v) { depth--; return v; },
        function (e) { depth--; throw e; }
      );
  }

  /* ---- state ---- */
  function wantedQty(cart) {
    var n = 0;
    (cart.items || []).forEach(function (it) {
      if (QUALIFY.indexOf(String(it.product_id)) !== -1) n += (it.quantity || 0);
    });
    return Math.min(n, MAX_QTY);
  }

  function warrantyLines(cart) {
    return (cart.items || []).filter(function (it) { return String(it.variant_id) === WTY; });
  }

  /* ---- sync ---- */
  function sync(reason) {
    if (inFlight) return inFlight;
    if (syncCount >= MAX_SYNCS) { log('circuit breaker tripped; standing down'); return Promise.resolve(false); }
    syncCount++;

    inFlight = getCart().then(function (cart) {
      var want = wantedQty(cart);
      var lines = warrantyLines(cart);
      var have = lines.reduce(function (s, l) { return s + l.quantity; }, 0);
      log(reason, 'want=' + want, 'have=' + have, 'lines=' + lines.length);

      /* Collapse accidental duplicate warranty lines (an app, or a stale tab, adding
         the variant with different line properties creates a second line). Set the
         survivor to the correct quantity in the SAME call - zeroing the extras alone
         would leave whatever quantity the rogue line happened to carry. */
      if (lines.length > 1) {
        var updates = {};
        updates[lines[0].key] = want;
        lines.slice(1).forEach(function (l) { updates[l.key] = 0; });
        return post(CART + '/update.js', { updates: updates }).then(function () { return true; });
      }
      if (have === want) return false;
      if (want === 0) return post(CART + '/change.js', { id: lines[0].key, quantity: 0 }).then(function () { return true; });
      if (!lines.length) {
        return post(CART + '/add.js', { items: [{ id: Number(WTY), quantity: want, properties: PROPS }] })
          .then(function () { return true; })
          .catch(function (e) { log('add failed', e); return false; });
      }
      return post(CART + '/change.js', { id: lines[0].key, quantity: want }).then(function () { return true; });
    }).then(function (changed) {
      inFlight = null;
      if (changed) return announce();
      return false;
    }).catch(function (e) {
      inFlight = null;
      log('sync error', e);
      return false;
    });

    return inFlight;
  }

  function announce() {
    return getCart().then(function (cart) {
      try {
        if (window.theme && window.theme.pubsub) {
          window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'fcp-warranty', cart: cart });
        }
      } catch (e) {}
      try { document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } })); } catch (e) {}
      paintCartLine();
      paintStack(cart);
      /* the cart page renders line items server-side, so a re-render is the only honest refresh */
      if (CFG.isCartPage) window.location.reload();
      return true;
    });
  }

  function schedule(reason, ms) {
    clearTimeout(timer);
    timer = setTimeout(function () { sync(reason); }, ms == null ? 350 : ms);
  }

  /* ---- observers ---- */
  var origFetch = window.fetch;
  if (typeof origFetch === 'function') {
    window.fetch = function (input, init) {
      var url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) {}
      var p = origFetch.apply(this, arguments);
      if (depth === 0 && /\/cart\/(add|change|update|clear)/.test(url)) {
        try { p.then(function () { schedule('fetch ' + url); }, function () {}); } catch (e) {}
      }
      return p;
    };
  }

  var OpenXHR = window.XMLHttpRequest && window.XMLHttpRequest.prototype.open;
  if (OpenXHR) {
    window.XMLHttpRequest.prototype.open = function (method, url) {
      try {
        if (depth === 0 && typeof url === 'string' && /\/cart\/(add|change|update|clear)/.test(url)) {
          this.addEventListener('load', function () { schedule('xhr ' + url); });
        }
      } catch (e) {}
      return OpenXHR.apply(this, arguments);
    };
  }

  try {
    if (window.theme && window.theme.pubsub) {
      window.theme.pubsub.subscribe(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, function (evt) {
        if (evt && evt.source === 'fcp-warranty') return;
        schedule('pubsub');
        if (evt && evt.cart) paintStack(evt.cart);
      });
    }
  } catch (e) {}

  /* non-AJAX form posts to /cart/add navigate, so the next page load covers them */
  window.addEventListener('pageshow', function () { schedule('pageshow', 120); });
  setInterval(function () { syncCount = 0; }, 60000);

  /* ---- presentation ---- */
  function paintCartLine() {
    if (!CFG.warrantyHandle) return;
    var rows = document.querySelectorAll('.cart-item');
    Array.prototype.forEach.call(rows, function (row) {
      var a = row.querySelector('a[href*="' + CFG.warrantyHandle + '"]');
      if (!a) return;
      row.classList.add('fcp-wty-line');
      if (row.querySelector('.fcp-wty-cartnote')) return;
      var host = row.querySelector('.cart-item__details') || a.parentNode;
      var note = document.createElement('span');
      note.className = 'fcp-wty-cartnote';
      var b = document.createElement('b');
      b.textContent = 'INCLUDED';
      note.appendChild(b);
      note.appendChild(document.createTextNode(' '));
      if (CFG.valueLabel) {
        var s = document.createElement('s');
        s.textContent = CFG.valueLabel;
        note.appendChild(s);
        note.appendChild(document.createTextNode(' '));
      }
      var sp = document.createElement('span');
      sp.textContent = CFG.cartNote || 'Free with your Fire Cold Plunge';
      note.appendChild(sp);
      host.appendChild(note);
    });
  }

  /* flip the stack pill between the pre-add and in-cart states */
  function paintStack(cart) {
    var pills = document.querySelectorAll('[data-fcp-pill]');
    if (!pills.length) return;
    var on = warrantyLines(cart || { items: [] }).length > 0;
    Array.prototype.forEach.call(pills, function (el) {
      el.textContent = on ? (CFG.pillOn || 'In your cart') : (CFG.pillOff || 'Added automatically');
      el.classList.toggle('fcp-vstack__pill--pending', !on);
    });
  }

  function boot() {
    paintCartLine();
    getCart().then(function (c) { paintStack(c); }).catch(function () {});
    sync('boot');
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.FCP_WARRANTY = { sync: sync, getCart: getCart, config: CFG };
}());
